import type { ReactNode } from "react"
import { usePage } from "@inertiajs/react"
import { cn } from "@/lib/utils"
import { SeoHead } from "@/components/SeoHead"
import { organizationNode } from "@/lib/seo"
import type { PageProps } from "@/types/inertia"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { track } from "@/lib/analytics"
import { CADENCE, EVERY_EPISODE, LAUNCH, OFFERS, SETUP, inclusions, type OfferKey } from "@/lib/offers"

// Client proposal at /proposal/:slug. Unlisted and noindex, sent by hand.
//
// The props come from config/proposals/<slug>.yml (see app/models/proposal.rb).
// The client-specific sections are optional and skipped when absent. The
// package sections are not: cadence, what every episode gets, the tier's
// inclusions and setup all come from @/lib/offers, so every proposal offers
// one of the two /pricing packages in the same words.

// Harry's booking link, the same one used in outreach emails.
const BOOKING_URL = "https://cal.com/harry-duran/60m"

type Figure = { value: string; label: string }
type Source = { label: string; url: string }
type GapRow = { show: string; serves: string; differs: string }
type Format = { name: string; meta?: string; body: string; flagship?: boolean }
type Episode = { title: string; format: string }

type ProposalData = {
  slug: string
  client: string
  contact: string
  prepared_on: string
  status?: string
  tier: OfferKey
  show_pricing?: boolean
  show: {
    title: string
    alternatives?: string[]
    tagline?: string
    host?: string
    listener_next_step?: string
  }
  heard?: { quote?: string; summary?: string }
  why_now?: { intro?: string; figures?: Figure[]; outro?: string; sources?: Source[] }
  gap?: { intro?: string; rows?: GapRow[]; outro?: string }
  formats?: Format[]
  episodes?: Episode[]
  guests?: string[]
  success?: string
  questions?: string[]
}

function formatDate(iso: string) {
  // Parse as a calendar date, not a timestamp, so SSR and the browser agree.
  const [y, m, d] = iso.split("-").map(Number)
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  return `${d} ${months[m - 1]} ${y}`
}

function Numbered({ n, children }: { n: number; children: ReactNode }) {
  return (
    <div className="max-w-2xl">
      <Eyebrow>{String(n).padStart(2, "0")}</Eyebrow>
      <h2 className="mt-3">{children}</h2>
    </div>
  )
}

export default function Proposal({ proposal }: { proposal: ProposalData }) {
  const base = (usePage<PageProps>().props.app_url || "https://fullcast.co").replace(/\/$/, "")
  const offer = OFFERS[proposal.tier]
  const p = proposal
  const description = `A podcast proposal for ${p.client}: ${p.show.title}, produced weekly by FullCast.`

  // Number only the sections that render, so the sequence never skips.
  let n = 0
  const next = () => ++n

  return (
    <MarketingLayout>
      <SeoHead title={`${p.show.title} · Proposal for ${p.client}`} description={description} noindex jsonLd={[organizationNode(base)]} />

      <Section containerClassName="max-w-3xl">
        <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-hairline bg-hairline text-sm sm:grid-cols-4">
          {[
            ["Prepared for", `${p.contact}, ${p.client}`],
            ["Prepared by", "Harry Duran, FullCast"],
            ["Date", formatDate(p.prepared_on)],
            ["Status", p.status || "For discussion"],
          ].map(([label, value]) => (
            <div key={label} className="min-w-0 bg-page px-4 py-3">
              <dt className="text-xs uppercase tracking-[0.1em] text-ink-muted">{label}</dt>
              <dd className="mt-1 text-ink-display">{value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-12">
          <Eyebrow>Podcast proposal · {p.client}</Eyebrow>
          <h1 className="display-1 mt-4">{p.show.title}</h1>
          {p.show.tagline && <p className="mt-5 text-lg text-ink-body">{p.show.tagline}</p>}
        </div>
      </Section>

      {p.heard && (
        <Section className="border-t border-hairline" containerClassName="max-w-3xl">
          <Numbered n={next()}>What you told us</Numbered>
          {p.heard.quote && <blockquote className="mt-6">"{p.heard.quote}"</blockquote>}
          {p.heard.summary && <p className="mt-6">{p.heard.summary}</p>}
        </Section>
      )}

      {p.why_now && (
        <Section className="border-t border-hairline bg-surface" containerClassName="max-w-3xl">
          <Numbered n={next()}>Why this audience, and why now</Numbered>
          {p.why_now.intro && <p className="mt-5">{p.why_now.intro}</p>}
          {p.why_now.figures && p.why_now.figures.length > 0 && (
            <div className="mt-8 grid gap-px overflow-hidden rounded-xl border border-hairline bg-hairline sm:grid-cols-3">
              {p.why_now.figures.map((f) => (
                <div key={f.value} className="min-w-0 bg-page p-5">
                  <div className="font-display text-3xl font-semibold tabular-nums text-ink-display">{f.value}</div>
                  <p className="mt-2 text-sm text-ink-muted">{f.label}</p>
                </div>
              ))}
            </div>
          )}
          {p.why_now.outro && <p className="mt-8">{p.why_now.outro}</p>}
          {p.why_now.sources && p.why_now.sources.length > 0 && (
            <p className="mt-4 text-sm text-ink-muted">
              Sources:{" "}
              {p.why_now.sources.map((s, i) => (
                <span key={s.url}>
                  {i > 0 && " · "}
                  <a href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a>
                </span>
              ))}
            </p>
          )}
        </Section>
      )}

      {p.gap && (
        <Section className="border-t border-hairline" containerClassName="max-w-3xl">
          <Numbered n={next()}>The gap in the market</Numbered>
          {p.gap.intro && <p className="mt-5">{p.gap.intro}</p>}
          {p.gap.rows && p.gap.rows.length > 0 && (
            <div className="mt-8 overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-left text-sm">
                <thead>
                  <tr>
                    {["Show", "Who it serves", `Where ${p.show.title} differs`].map((h) => (
                      <th key={h} className="border-b-2 border-ink-display px-3 py-3 text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {p.gap.rows.map((r) => (
                    <tr key={r.show}>
                      <td className="border-b border-hairline px-3 py-3 align-top font-medium text-ink-display">{r.show}</td>
                      <td className="border-b border-hairline px-3 py-3 align-top text-ink-body">{r.serves}</td>
                      <td className="border-b border-hairline px-3 py-3 align-top text-ink-body">{r.differs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {p.gap.outro && <p className="mt-8">{p.gap.outro}</p>}
        </Section>
      )}

      <Section className="border-t border-hairline bg-surface" containerClassName="max-w-3xl">
        <Numbered n={next()}>The show</Numbered>
        <dl className="mt-8 grid gap-5 rounded-xl border border-accent bg-accent-faded p-6">
          {[
            ["Title", [p.show.title, p.show.alternatives?.length ? ` (alternatives: ${p.show.alternatives.join(", ")})` : ""].join("")],
            ["Host", p.show.host],
            ["Episodes", `${CADENCE}, published to YouTube, Apple, Spotify and every other directory`],
            ["Launch", LAUNCH],
            ["Next step for listeners", p.show.listener_next_step],
            ["How we work", `The FullCast ${offer.name} (${offer.term.toLowerCase()})`],
          ]
            .filter(([, v]) => v)
            .map(([label, value]) => (
              <div key={label} className="grid gap-1 sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-6">
                <dt className="text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted sm:pt-1">{label}</dt>
                <dd className="min-w-0 text-ink-body">{value}</dd>
              </div>
            ))}
        </dl>
      </Section>

      {p.formats && p.formats.length > 0 && (
        <Section className="border-t border-hairline" containerClassName="max-w-3xl">
          <Numbered n={next()}>Recurring formats</Numbered>
          <p className="mt-5">Each one fills the weekly slot in rotation, and each is a sample of something you already offer.</p>
          <div className="mt-8 grid gap-8 sm:grid-cols-2">
            {p.formats.map((f) => (
              <article key={f.name} className={cn("min-w-0 border-t pt-4", f.flagship ? "border-t-2 border-accent" : "border-hairline")}>
                <h3>{f.name}</h3>
                {f.meta && <p className="mt-1 text-sm text-ink-muted">{f.meta}</p>}
                <p className="mt-3">{f.body}</p>
              </article>
            ))}
          </div>
        </Section>
      )}

      {p.episodes && p.episodes.length > 0 && (
        <Section className="border-t border-hairline bg-surface" containerClassName="max-w-3xl">
          <Numbered n={next()}>The first {p.episodes.length} weeks</Numbered>
          <p className="mt-5">One new video episode every week after the trailer. Working titles, to shape together.</p>
          <div className="mt-8 overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-left text-sm">
              <thead>
                <tr>
                  {["Week", "Working title", "Format"].map((h) => (
                    <th key={h} className="border-b-2 border-ink-display px-3 py-3 text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {p.episodes.map((e, i) => (
                  <tr key={e.title}>
                    <td className="w-14 border-b border-hairline px-3 py-3 align-top tabular-nums text-accent-display">{String(i + 1).padStart(2, "0")}</td>
                    <td className="border-b border-hairline px-3 py-3 align-top text-ink-body">{e.title}</td>
                    <td className="border-b border-hairline px-3 py-3 align-top whitespace-nowrap text-ink-muted">{e.format}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <Section className="border-t border-hairline" containerClassName="max-w-3xl">
        <Numbered n={next()}>Guests, reach and results</Numbered>
        <div className="mt-8 grid gap-10 sm:grid-cols-2">
          <div className="min-w-0">
            <h3>Guests become conversations</h3>
            <p className="mt-3">
              {proposal.tier === "growth_partnership"
                ? "Each month we research and prioritise guests and write the outreach. The invitations go out from you."
                : "You choose who comes on. We handle everything from the raw recording onward."}
            </p>
            {p.guests && p.guests.length > 0 && (
              <ul className="mt-4 space-y-2">
                {p.guests.map((g) => <li key={g}>{g}</li>)}
              </ul>
            )}
          </div>
          <div className="min-w-0">
            <h3>What every episode gets</h3>
            <ul className="mt-4 space-y-2">
              {EVERY_EPISODE.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </div>
        {p.success && <p className="mt-10">{p.success}</p>}
      </Section>

      <Section className="border-t border-hairline bg-surface" containerClassName="max-w-3xl">
        <Numbered n={next()}>How we'd work together</Numbered>
        <p className="mt-5">
          This plan fits our <strong>{offer.name}</strong>. {offer.summary}
        </p>
        <div className="mt-8 grid gap-10 sm:grid-cols-2">
          <div className="min-w-0">
            <h3>FullCast</h3>
            <ul className="mt-4 space-y-2">
              {inclusions(proposal.tier).map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
          <div className="min-w-0">
            <h3>You</h3>
            <p className="mt-4">You own {offer.clientOwns}</p>
            <h3 className="mt-8">Setup, before the first episode</h3>
            <ul className="mt-4 space-y-2">
              {SETUP.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </div>
        {p.show_pricing && (
          <div className="mt-10 border-t border-hairline pt-6 tabular-nums">
            <span className="font-display text-4xl font-semibold text-ink-display">{offer.price}</span>
            <span className="text-ink-muted"> / month</span>
            <p className="mt-2 text-sm text-ink-muted">{offer.term}, {offer.setup}</p>
          </div>
        )}
      </Section>

      {p.questions && p.questions.length > 0 && (
        <Section className="border-t border-hairline" containerClassName="max-w-3xl">
          <Numbered n={next()}>To decide together</Numbered>
          <ol className="mt-6 space-y-2">
            {p.questions.map((q) => <li key={q}>{q}</li>)}
          </ol>
        </Section>
      )}

      <Section className="border-t border-hairline bg-surface" containerClassName="max-w-3xl">
        <h2>Next step</h2>
        <p className="mt-5 text-lg text-ink-body">
          A call to walk through the plan and shape the first episodes together.
        </p>
        <div className="mt-8">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("CTA Click")}
            className="inline-block rounded-xl bg-ink-display px-6 py-3 text-page no-underline hover:bg-ink-body"
          >
            Book a call with Harry →
          </a>
        </div>
      </Section>
    </MarketingLayout>
  )
}
